import {
  ClerkFailed,
  ClerkLoaded,
  ClerkLoading,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useUser
} from '@clerk/clerk-react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { SnackbarProvider } from 'notistack'
import { Suspense, useEffect } from 'react'
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration } from 'react-router-dom'
import { ErrorBoundary } from './components/common/ErrorBoundary.tsx'
import LoadingSpinner from './components/common/LoadingSpinner.tsx'
import DashboardPage from './components/pages/dashboard/index.tsx'
import DeduplicationPage from './components/pages/deduplication/index.tsx'
import LinkReviewPage from './components/pages/deduplication/link-review.tsx'
import MergeReviewPage from './components/pages/deduplication/merge-review.tsx'
import ErrorPage from './components/pages/error.tsx'
import GPDetailPage from './components/pages/gps/detail.tsx'
import GPSPage from './components/pages/gps/GPsPage.tsx'
import InvestigationDetailPage from './components/pages/investigations/detail.tsx'
import RootLayout from './components/pages/layout.tsx'
import MedRequestDetailPage from './components/pages/med-requests/detail.tsx'
import MedRequestsPage from './components/pages/med-requests/index.tsx'
import OrderDetailPage from './components/pages/orders/detail.tsx'
import OrdersPage from './components/pages/orders/index.tsx'
import PatientDetailPage from './components/pages/patients/detail.tsx'
import PatientsPage from './components/pages/patients/index.tsx'
import ProductDetailPage from './components/pages/products/detail.tsx'
import ProductsPage from './components/pages/products/index.tsx'
import RingCentralIncomingPage from './components/pages/ringcentral/incoming.tsx'
import SubscriptionDetailPage from './components/pages/subscriptions/detail.tsx'
import UserDetailPage from './components/pages/users/detail.tsx'
import UsersPage from './components/pages/users/index.tsx'
import Protect from './components/Protect.tsx'
import { theme } from './theme.ts'

const router = createBrowserRouter([
  {
    element: (
      <RootLayout>
        <ScrollRestoration />
        <Suspense fallback={<LoadingSpinner />}>
          <Protect
            roles={['CS_MANAGER', 'CS_AGENT', 'CS_PHARMACY_MANAGER', 'LEAD_PRESCRIBER', 'PHARMACIST', 'CS_VIEWER']}
            fallback='You do not have permission to access this application'
          >
            <ErrorBoundary fallback='Error loading the page'>
              <Outlet />
            </ErrorBoundary>
          </Protect>
        </Suspense>
      </RootLayout>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <DashboardPage />,
        errorElement: <ErrorPage />
      },
      {
        path: '/deduplication',
        element: <DeduplicationPage />
      },
      {
        path: '/deduplication/merge/:groupId',
        element: <MergeReviewPage />
      },
      {
        path: '/deduplication/link/:groupId',
        element: <LinkReviewPage />
      },
      {
        path: '/patients',
        element: <PatientsPage />
      },
      {
        path: '/patients/:hash',
        element: <PatientDetailPage />
      },
      {
        path: '/users',
        element: <UsersPage />
      },
      {
        path: '/users/:email',
        element: <UserDetailPage />
      },
      {
        path: '/med-requests',
        element: <MedRequestsPage />
      },
      {
        path: '/med-requests/:id',
        element: <MedRequestDetailPage />
      },
      {
        path: '/orders',
        element: <OrdersPage />
      },
      {
        path: '/orders/:hash',
        element: <OrderDetailPage />
      },
      {
        path: '/subscriptions/:hash',
        element: <SubscriptionDetailPage />
      },
      {
        path: '/gps',
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <GPSPage />
          </Suspense>
        )
      },
      {
        path: '/gps/:hash',
        element: <GPDetailPage />
      },
      {
        path: '/investigations/:id',
        element: <InvestigationDetailPage />
      },
      {
        path: '/products',
        element: <ProductsPage />
      },
      {
        path: '/products/:id',
        element: <ProductDetailPage />
      },
      {
        path: '/ringcentral/incoming',
        element: <RingCentralIncomingPage />
      },
      {
        path: '*',
        element: <h1>Route Not found</h1>
      }
    ]
  }
])

function App() {
  const { user } = useUser()
  const primaryEmail = user?.primaryEmailAddress?.emailAddress

  useEffect(() => {
    if (!user) return

    if (!primaryEmail) {
      console.warn('failed to load user data, reloading user...')
      user.reload()
    }
  }, [user, primaryEmail])

  return (
    <SnackbarProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ClerkLoaded>
          <SignedOut>
            <RedirectToSignIn />
          </SignedOut>
          {primaryEmail && (
            <SignedIn>
              <RouterProvider router={router} />
            </SignedIn>
          )}
        </ClerkLoaded>
        <ClerkLoading>
          <LoadingSpinner text='Loading Authentication' />
        </ClerkLoading>
        <ClerkFailed>
          <h1>Failed to Load Authentication, try refreshing the page</h1>
        </ClerkFailed>
      </ThemeProvider>
    </SnackbarProvider>
  )
}

export default App
